import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaClock, FaSignOutAlt, FaRedo } from 'react-icons/fa';
import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@theme/useTheme';
import { useAppSelector } from '../../store/hooks';
import AuthProtected from './AuthProtected';

interface SessionTimeoutProps {
    warningTimeMinutes?: number;
    onTimeout?: () => void;
}

const SessionTimeoutComponent: React.FC<SessionTimeoutProps> = ({
    warningTimeMinutes = 5,
    onTimeout
}) => {
    const { logout } = useAuth();
    const { colors } = useTheme();
    const { sessionExpiry } = useAppSelector((state) => state.auth);
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number>(0);

    useEffect(() => {
        if (!sessionExpiry) return;

        const checkSession = () => {
            const now = Date.now();
            const timeRemaining = sessionExpiry - now;

            if (timeRemaining <= 0) {
                // Session expired
                handleTimeout();
                return;
            }

            const warningTime = warningTimeMinutes * 60 * 1000; // Convert to milliseconds

            if (timeRemaining <= warningTime) {
                setShowWarning(true);
                setTimeLeft(Math.ceil(timeRemaining / 1000)); // Convert to seconds
            } else {
                setShowWarning(false);
            }
        };

        // Check immediately
        checkSession();

        // Set up interval to check every minute
        const interval = setInterval(checkSession, 60000);

        return () => clearInterval(interval);
    }, [sessionExpiry, warningTimeMinutes]);

    const handleTimeout = async () => {
        try {
            await logout();
            onTimeout?.();
        } catch (error) {
            console.error('Logout failed during timeout:', error);
        }
    };

    const handleExtendSession = () => {
        // This would typically refresh the session
        // For now, we'll just hide the warning
        setShowWarning(false);
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    return (
        <AnimatePresence>
            {showWarning && (
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    className="fixed bottom-4 right-4 z-50 max-w-sm w-full"
                >
                    <div
                        className="border rounded-lg shadow-lg p-4"
                        style={{
                            backgroundColor: colors.input,
                            borderColor: colors.error
                        }}
                    >
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <FaClock className="h-5 w-5" style={{ color: colors.error }} />
                            </div>

                            <div className="ml-3 flex-1">
                                <h3
                                    className="text-sm font-medium"
                                    style={{ color: colors.heading }}
                                >
                                    Session Expiring Soon
                                </h3>

                                <div className="mt-2 text-sm">
                                    <p style={{ color: colors.text }}>
                                        Your session will expire in {formatTime(timeLeft)}.
                                        Please save your work and refresh to extend your session.
                                    </p>
                                </div>

                                <div className="mt-3 flex space-x-2">
                                    <button
                                        onClick={handleExtendSession}
                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                                        style={{
                                            color: colors.primary,
                                            backgroundColor: colors.secondaryButton
                                        }}
                                    >
                                        <FaRedo className="mr-1" size={12} />
                                        Extend Session
                                    </button>

                                    <button
                                        onClick={handleTimeout}
                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md focus:outline-none"
                                        style={{
                                            color: colors.error
                                        }}
                                    >
                                        <FaSignOutAlt className="mr-1" size={12} />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const SessionTimeout: React.FC<SessionTimeoutProps> = (props) => (
    <AuthProtected fallback={null}>
        <SessionTimeoutComponent {...props} />
    </AuthProtected>
);

export default SessionTimeout;
